/**
 * Turns a campaign's change history into entries for the shared audit log,
 * with each value written the way staff read it: money and percent as
 * discounts, instants as Eastern dates and times, the switch as on or off.
 */
import {
  campaignDiscountLabel,
  formatCampaignDate,
  formatCampaignTime,
  TICKET_SALES_CAMPAIGN_AUDITED_FIELDS,
  TicketSalesCampaignDiscountType,
  TicketSalesCampaignEditEventType,
  type TicketSalesCampaignAuditedField,
  type TicketSalesCampaignEditEvent,
  type TicketSalesCampaignFieldValue,
} from '@bonsai/shared';

import type { AuditEntry } from '../../audit/AuditLog';

const FIELD_LABELS: Record<TicketSalesCampaignAuditedField, string> = {
  name: 'Name',
  description: 'Banner description',
  discountType: 'Discount type',
  discountValue: 'Discount',
  startsAt: 'Starts',
  endsAt: 'Ends',
  enabled: 'Enabled',
};

const TYPE_LABELS: Record<TicketSalesCampaignDiscountType, string> = {
  [TicketSalesCampaignDiscountType.PERCENT]: 'Percentage',
  [TicketSalesCampaignDiscountType.FLAT]: 'Flat amount',
};

/** "Fri, Oct 16, 2026 at 4:39 PM ET". */
export function formatEasternMoment(instant: string | Date): string {
  return `${formatCampaignDate(instant)} at ${formatCampaignTime(instant)} ET`;
}

function isDiscountType(value: unknown): value is TicketSalesCampaignDiscountType {
  return (
    value === TicketSalesCampaignDiscountType.PERCENT ||
    value === TicketSalesCampaignDiscountType.FLAT
  );
}

function formatDiscount(
  field: 'discountType' | 'discountValue',
  value: string | number | boolean,
  discountType: TicketSalesCampaignDiscountType,
): string {
  if (field === 'discountType') return isDiscountType(value) ? TYPE_LABELS[value] : String(value);
  return typeof value === 'number'
    ? campaignDiscountLabel({ discountType, discountValue: value })
    : String(value);
}

function formatValue(
  field: TicketSalesCampaignAuditedField,
  value: TicketSalesCampaignFieldValue,
  discountType: TicketSalesCampaignDiscountType,
): string | null {
  if (value === null || value === '') return null;
  switch (field) {
    case 'discountType':
    case 'discountValue':
      return formatDiscount(field, value, discountType);
    case 'startsAt':
    case 'endsAt':
      return formatEasternMoment(String(value));
    case 'enabled':
      return value ? 'On' : 'Off';
    default:
      return String(value);
  }
}

/**
 * The discount type before and after each event, worked out by replaying the
 * history from its start: an amount only reads as "20% off" or "$20 off" once
 * you know which type it was entered under.
 */
function discountTypesByEvent(
  oldestFirst: TicketSalesCampaignEditEvent[],
  fallback: TicketSalesCampaignDiscountType,
): Map<
  string,
  { before: TicketSalesCampaignDiscountType; after: TicketSalesCampaignDiscountType }
> {
  const types = new Map<
    string,
    { before: TicketSalesCampaignDiscountType; after: TicketSalesCampaignDiscountType }
  >();
  let current = fallback;
  for (const event of oldestFirst) {
    const change = event.fieldChanges.discountType;
    const before = isDiscountType(change?.before) ? change.before : current;
    const after = isDiscountType(change?.after) ? change.after : before;
    types.set(event.id, { before, after });
    current = after;
  }
  return types;
}

/**
 * Audit-log entries for a campaign, newest first as the API sends them.
 * `currentType` reads amounts when the history does not say which type they
 * were entered under.
 */
export function campaignHistoryEntries(
  events: TicketSalesCampaignEditEvent[],
  currentType: TicketSalesCampaignDiscountType,
): AuditEntry[] {
  const types = discountTypesByEvent([...events].reverse(), currentType);
  return events.map((event) => {
    const type = types.get(event.id) ?? { before: currentType, after: currentType };
    const created = event.eventType === TicketSalesCampaignEditEventType.CREATED;
    const fieldChanges: Record<string, { before: string | null; after: string | null }> = {};
    for (const field of TICKET_SALES_CAMPAIGN_AUDITED_FIELDS) {
      const change = event.fieldChanges[field];
      if (!change) continue;
      fieldChanges[FIELD_LABELS[field]] = {
        before: formatValue(field, change.before, type.before),
        after: formatValue(field, change.after, type.after),
      };
    }
    return {
      id: event.id,
      actorUserId: event.actor.id,
      actorEmail: null,
      actorName: event.actor.name,
      changedAt: event.changedAt,
      fieldChanges,
      summary: created ? 'created the campaign' : undefined,
      hideBefore: created,
    };
  });
}
